import { useHistory, useParams } from "react-router-dom";
import { useAuth } from "../../hooks/auth";
import { useToast } from "../../hooks/toast";
import React, { ChangeEvent, useState } from "react";
import refreshToken from "../../utils/refreshToken";
import api from "../../services/api";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import { ErrorMessageContainer } from "../ShowContractTemplates/styles";
import {
  Wrapper,
  Container,
  FormTitle,
  FormParagraph,
  ButtonsWrapper,
  BackBtn,
  ForwardBtn,
  FormWrapper,
  AddSignerBtn,
  RemoveSignerBtn,
  SignerButtonsWrapper,
  AttachFileButton,
  FilesCheckWrapper,
  FileChecked,
} from "./styles";
import {
  MdChevronLeft,
  MdFileUpload,
  MdOutlineAttachment,
  MdPersonOutline,
} from "react-icons/md";
import { applyMask } from "../../utils/mask";
import { regexMap } from "../../utils/regexMapper";
import { SimpleFormField } from "../../global/types";
import FormField from "./components/FormField";
import { FiCheck, FiX } from "react-icons/fi";

type FormattedSigner = {
  name: string;
  email: string;
};

const CLIENT_FIELDS = 2;

function formatSigners(signers: SimpleFormField[]) {
  let formattedSigners: FormattedSigner[] = [];
  for (let i = 0; i < signers.length; i++) {
    const index = Math.floor(i / CLIENT_FIELDS);
    const value = signers[i].value;

    if (!formattedSigners[index]) {
      formattedSigners[index] = { name: "", email: "" };
    }

    switch (i % CLIENT_FIELDS) {
      case 0:
        formattedSigners[index].name = value;
        break;
      case 1:
        formattedSigners[index].email = value;
        break;
    }
  }

  return formattedSigners;
}

const DEFAULT_SIGNERS: SimpleFormField[] = [
  {
    label: `NOME_COMPLETO_7cantos`,
    type: "texto",
    error: "",
    value: "",
  },
  {
    label: `E-MAIL_7CANTOS`,
    type: "texto",
    error: "",
    value: "",
  },
  {
    label: `NOME_COMPLETO_CLIENTE_1`,
    type: "texto",
    error: "",
    value: "",
  },
  {
    label: `E-MAIL_CLIENTE_1`,
    type: "texto",
    error: "",
    value: "",
  },
];

export default function SendContractToSigners() {
  const { access_token, dateToExpires } = useAuth();
  const { addToast } = useToast();
  const history = useHistory();

  const [sendingContract, setSendingContract] = useState(false);
  const [loadApiError, setLoadApiError] = useState(false);
  const [contract, setContract] = useState<File | undefined>();
  const [signers, setSigners] = useState<SimpleFormField[]>(DEFAULT_SIGNERS);

  function handleBlur(index: number, type: string) {
    setSigners((prevData) => {
      const updatedData = [...prevData];

      const value = updatedData[index].value;

      const regex = regexMap[type];

      if (regex && !regex.test(value)) {
        updatedData[index].error = `${type.toLocaleUpperCase()} inválido`;
      } else {
        updatedData[index].error = "";
      }

      return updatedData;
    });
  }

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    type: string
  ) {
    setSigners((prevData) => {
      const newData = [...prevData];
      newData[index] = {
        ...newData[index],
        value: applyMask(type, e.target.value),
      };
      return newData;
    });
  }

  function handleGoBack() {
    history.goBack();
  }

  async function handleSendContractToSigners(
    event: React.FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();
    let hasError = false;
    for (const key in signers) {
      if (signers[key].error !== "" || signers[key].value === "") {
        hasError = true;
        break;
      }
    }
    if (!contract || contract.type !== "application/pdf") hasError = true;

    if (hasError) {
      addToast({
        title: "Formulário incompleto",
        description:
          "Preencha todos os campos corretamente e anexe o contrato em pdf",
        type: "error",
      });
      return;
    }

    const body = new FormData();
    body.append("signers", JSON.stringify(formatSigners(signers)));

    if (contract) {
      body.append("contract", contract);
    }
    setSendingContract(true);
    addToast({
      title: "Enviando contrato",
      description: "Seu contrato está sendo enviado...",
      type: "info",
    });
    try {
      const token = await refreshToken(dateToExpires, access_token);

      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      const response = await api.post(`/api/adm/contracts/sendToSigners`, body);
      if (response.data.data.success)
        addToast({
          title: "Contrato Enviado com Sucesso!",
          type: "success",
        });
    } catch (err: any) {
      if (!!err.response?.statusText) {
        addToast({
          title: "Erro ao enviar o contrato!",
          description: err.response.statusText,
          type: "error",
        });
      } else if (!!err.response?.data.message) {
        setLoadApiError(err.response?.data.message);
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      }
    }
    setSendingContract(false);
  }

  function handleAddSigner() {
    setSigners((prevArray) => {
      const signerNumber = prevArray.length / CLIENT_FIELDS;
      const defaultSigner: SimpleFormField[] = [
        {
          label: `NOME_COMPLETO_CLIENTE_${signerNumber}`,
          type: "texto",
          error: "",
          value: "",
        },
        {
          label: `E-MAIL_CLIENTE_${signerNumber}`,
          type: "texto",
          error: "",
          value: "",
        },
      ];

      return [...prevArray, ...defaultSigner];
    });
  }

  function handleRemoveSigner() {
    setSigners((prevArray) => prevArray.slice(0, -CLIENT_FIELDS));
  }

  async function handleContractUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file && file.type === "application/pdf") {
      setContract(file);
    } else {
      addToast({
        title: "Erro ao fazer o upload!",
        type: "error",
        description: "O arquivo enviado deve estar em formato PDF",
      });
    }
  }

  return (
    <Container>
      <Header />
      {loadApiError && (
        <ErrorMessageContainer>
          <h2>{loadApiError}</h2>
        </ErrorMessageContainer>
      )}
      <FormTitle>Signatários</FormTitle>
      <FormParagraph>Por favor, preencha os campos abaixo:</FormParagraph>
      {!loadApiError && (
        <FormWrapper onSubmit={handleSendContractToSigners}>
          <Wrapper>
            {signers.map((contractField, index) => (
              <FormField
                key={contractField.label}
                contractField={contractField}
                onChange={(e) => handleChange(e, index, contractField.type)}
                onBlur={() => handleBlur(index, contractField.type)}
              />
            ))}
          </Wrapper>
          <SignerButtonsWrapper>
            <RemoveSignerBtn
              onClick={handleRemoveSigner}
              type="button"
              disabled={signers.length <= 2 * CLIENT_FIELDS}
            >
              -
            </RemoveSignerBtn>
            <MdPersonOutline />
            <AddSignerBtn onClick={handleAddSigner} type="button">
              +
            </AddSignerBtn>
          </SignerButtonsWrapper>
          <FilesCheckWrapper>
            {contract && (
              <FileChecked key={contract.name}>
                <FiCheck size={20} color="#00BF33" />
                <p>{contract.name}</p>

                <button type="button" onClick={() => setContract(undefined)}>
                  <FiX size={12} color="#666666" />
                </button>
              </FileChecked>
            )}
          </FilesCheckWrapper>
          <ButtonsWrapper>
            <BackBtn onClick={handleGoBack} type="button">
              <MdChevronLeft />
              Voltar
            </BackBtn>
            <AttachFileButton>
              <label htmlFor="add-file-btn" id="add-file-lbl">
                <MdOutlineAttachment size={24} fontWeight={600} />
                Adicionar Contrato
              </label>
              <input
                type="file"
                id="add-file-btn"
                accept=".pdf"
                onChange={handleContractUpload}
              />
            </AttachFileButton>
            <ForwardBtn disabled={sendingContract}>
              {sendingContract ? "Enviando Contrato..." : "Enviar Contrato"}
              <MdFileUpload />
            </ForwardBtn>
          </ButtonsWrapper>
        </FormWrapper>
      )}
      <Footer />
    </Container>
  );
}
